import { useMotionValue, useMotionValueEvent } from "framer-motion"
import { useLayoutEffect, useRef, useState } from "react"
import { css } from "@emotion/react"
import styled from "@emotion/styled"

import { a11yOutline } from "css/a11y"
import { body100, bold, legend100, normal, quote50, secondaryFont } from "css/text"
import getMediaQuery from "css/breakpoints"
import { green } from "css/buttons"

import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useNavigation } from "contexts/Navigation"
import { useLocale } from "contexts/Locale"
import { usePanelModal } from "contexts/PanelModal"

import Link from "components/link/Link"
import Image from "components/media/ImageCLD"
import Video, { cssContainer, cssControls } from "components/media/VideoCLD"
import { RANGE_INC, setProperty } from "./utils"

const Item = styled.li`
  position: relative;
  margin: var(--sg-gap-margin);
  --sg-item-background-main: #f9f9f9;
  --sg-item-background-quote: rgb(var(--pure-white));
  background-color: var(--sg-item-background);
  overflow: hidden;
  --sg-item-row: calc(var(--sg-item-row-1, 0) * var(--sg-row-span-1) + var(--sg-item-row-2, 0) * var(--sg-row-span-2) + 1);
  grid-area: var(--sg-item-row) / var(--sg-item-col, 0) / span var(--sg-item-row-span, 0);

  display: grid;
  ${green}

  opacity: 0;
  --sg-opacity-delay-in: 0.12s;
  --sg-opacity-delay-in-computed: calc(0.1s + var(--sg-item-index, 0) * (var(--sg-opacity-delay-in) - var(--sg-item-index, 0) * 0.005s));
  --sg-opacity-delay-out: 0.006s;
  --sg-opacity-delay-out-computed: calc(var(--sg-item-index, 0) * var(--sg-opacity-delay-out));
  --sg-opacity-delay: calc(var(--sg-opacity, 1) * var(--sg-opacity-delay-in-computed) + (1 - var(--sg-opacity, 1)) * var(--sg-opacity-delay-out-computed));
  --sg-opacity-duration-in: 0.4s;
  --sg-opacity-duration-out: 0.1s;
  --sg-opacity-duration: calc(var(--sg-opacity, 1) * var(--sg-opacity-duration-in));
  transition: opacity var(--sg-opacity-duration) cubic-bezier(1, 1, 1, 1) calc(var(--sg-opacity-delay) + 0.1s);
  opacity: calc(var(--sg-item-opacity, 0) * var(--sg-opacity, 1));

  --sg-card-offset-computed: calc(var(--sg-offset, 0) * var(--sg-on, 0) * 1px);
  --sg-card-y: calc(var(--sg-card-offset-computed) * var(--sg-y-progress, 0));
  transform: translateY(var(--sg-card-y));

  html.prefers-reduced-motion & {
    --sg-card-offset-computed: 0 !important;
  }

  @supports (animation-timeline: view()) {
    transform: none;
    animation: linear sg-card-move both;
    animation-duration: 1ms;
    animation-range: entry 100% entry-crossing 100%;
    animation-timeline: --sg-list;

    &[data-col="2"] {
      animation-timing-function: cubic-bezier(0.5, 1, 0.89, 1);
    }
    &[data-col="3"] {
      animation-timing-function: linear;
    }
    &[data-col="4"] {
      animation-timing-function: cubic-bezier(0.11, 0, 0.5, 0);
    }
    &[data-col="5"] {
      animation-timing-function: cubic-bezier(0.37, 0, 0.63, 1);
    }
    &[data-col="6"] {
      animation-timing-function: cubic-bezier(0.25, 1, 0.5, 1);
    }
  }

  .sg-straight & {
    transition: none;
    opacity: 1;
  }

  &:has(a.focus-visible, .sg-card-action.focus-visible) {
    ${a11yOutline}
    img {
      transform: scale(1.03);
    }
  }
`

const cardActionStyles = css`
  position: relative;
  grid-row: 1;
  grid-column: 1/-1;
  text-decoration: none;
  display: grid;
  pointer-events: none;
  color: inherit;
  text-wrap: pretty;
  text-wrap: balance;
  container-type: inline-size;

  &:not(.sg-quote):before {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    inset: 0;
    background: linear-gradient(transparent 50%, rgba(0 0 0 / 0.4));
  }

  &.sg-quote {
    pointer-events: auto;

    @media (hover: hover) {
      &:hover {
        cursor: pointer;
      }
    }
  }
`

const CardAction = styled.a`
  ${cardActionStyles}
`

// Used instead of CardAction when the content isn't available in the current locale (fallback languages modal).
const CardButton = styled.button`
  ${cardActionStyles}
  width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: none;
  font: inherit;
  text-align: inherit;
  appearance: none;
`

const Media = styled.figure`
  position: relative;
  grid-row: 1;
  grid-column: 1/-1;
  overflow: hidden;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      img {
        transform: scale(1.03);
      }
    }
  }

  & .${cssContainer} {
    height: 100%;
    display: block;
    transform: translateZ(0);

    & video {
      position: absolute;
      top: 0;
      width: calc(100% + 1px);
      height: calc(100% + 1px) !important;
      object-fit: cover;
    }

    & .${cssControls} {
      position: absolute;
      inset: auto 0 0 auto;
      margin-block-end: calc(var(--sg-gap) + 10px);
      margin-inline-end: calc(var(--sg-gap) + 10px);

      ${getMediaQuery("m")} {
        margin-block-end: var(--sg-gap);
        margin-inline-end: var(--sg-gap);
      }

      & button {
        transform: scale(0.75);
        transform-origin: 100% 100%;
        //        backdrop-filter: none;
      }
    }
  }

  & picture {
    display: contents;
  }

  & img,
  & video {
    position: absolute;
    top: 0;
    display: block;
    object-position: top;
    transition: transform 600ms cubic-bezier(0.33, 1, 0.68, 1);
    aspect-ratio: var(--sg-ratio);
    object-fit: cover;
  }

  & img {
    width: 100%;
    //    height: auto;
    height: 100%;
  }

  --sg-card-media-offset: -4%;
  --sg-card-media-offset-computed: var(--sg-card-media-offset);
  --sg-card-media-offset-range: min(40vh, calc(var(--maxwidth) / var(--sg-cols)));
  --sg-card-media-offset-start: calc(var(--sg-card-top) + var(--sg-card-y));
  --sg-card-media-offset-end: calc(var(--sg-card-media-offset-start) + var(--sg-card-media-offset-range));

  @supports (animation-timeline: view()) {
    animation: linear sg-card-media-move both;
    animation-duration: 1ms;
    animation-range: entry-crossing var(--sg-card-media-offset-start) var(--sg-card-media-offset-end);
    animation-timeline: --sg-list;
  }
`

const Text = styled.div`
  position: relative;
  pointer-events: none;
  grid-row: 1;
  grid-column: 1;
  align-self: var(--sg-item-v-align);
  text-align: center;
  padding-inline: calc(var(--sg-card-width) / 10);
  padding-block: calc(var(--sg-card-width) / 7.5);

  .quote-card & {
    padding-inline: 12px;
    padding-block: 1rem;
    text-align: center;
    align-self: center;
  }

  ${getMediaQuery("m")} {
    .quote-card & {
      padding-inline: calc(var(--sg-card-width) / 10);
      padding-block: 0;
    }
  }

  html.prefers-contrast & {
    color: black !important;
    position: relative;
    background: white;
  }
`

const Kicker = styled.span`
  display: block;
  ${normal}
  ${secondaryFont}
  --card-kicker-size: clamp(0.5rem, 0.25rem + 1.8519cqw, 0.6rem);
  font-size: var(--card-kicker-size);
  &:lang(ja) {
    font-size: calc(var(--card-kicker-size) * 1.4);
  }
  line-height: 1.43;
  padding-block-end: 0.25rem;
`

const Title = styled.p`
  font-size: clamp(1.2rem, -0.375rem + 10.3704cqw, 1.75rem);
  line-height: 1;
  font-weight: 700;

  ${getMediaQuery("m")} {
    padding-block-end: 0.625rem;
  }
`

const Quote = styled.blockquote`
  ${body100}
  ${bold}
  margin: 0;
  padding-block-end: 0.625rem;

  .quote-card & {
    text-align: center;
    align-self: center;
    ${quote50}
    font-size: 1rem;
  }

  ${getMediaQuery("m")} {
    .quote-card & {
      ${quote50}
      font-size: 32px;
    }
  }

  ::before {
    content: var(--quote-opening);
  }

  ::after {
    content: var(--quote-closing);
  }
`

const CTA = styled.p`
  ${legend100}
  ${bold};
  display: none;

  ${getMediaQuery("m")} {
    display: block;
  }
`

const SmallLink = styled(Link)`
  :not(.sg-quote) & {
    display: none !important;
  }

  ${getMediaQuery("m")} {
    display: initial !important;
  }

  &.inline {
    ${legend100}
    ${bold};
    padding-block-end: 0;

    svg {
      width: 10px;
      height: 10px;
      margin-inline: 0.25rem !important;
    }

    .focus-visible & {
      outline: 0 !important;
    }
  }
`
function SGImage(props) {
  const console = useConsole()

  return <Image {...props} sizes='auto' />
}

function usePanelModalChange(rfel, state) {
  const rfpaused = useRef(false)

  function onState(v) {
    if (!rfel.current) return
    const { isPlaying } = rfel.current.playerState
    if (v) {
      if (isPlaying) {
        rfel.current.pause()
        rfpaused.current = true
        return
      }
    }
    if (rfpaused.current) rfel.current.play()
    rfpaused.current = false
  }
  useMotionValueEvent(state, "change", onState)

  return null
}

function SGVideo(props) {
  const console = useConsole()
  const { focused } = props
  const rfvideo = useRef(null)

  function onFocused(v) {
    const { isLocked = false } = rfvideo.current?.playerState ?? {}
    if (isLocked) return
    rfvideo.current?.[v ? "play" : "pause"]()
  }
  useMotionValueEvent(focused, "change", onFocused)

  const { panelModalState } = usePanelModal()
  usePanelModalChange(rfvideo, panelModalState)

  return <Video ref={rfvideo} {...props} autoPlay={false} unbehaving={true} noButton={true} />
}

export default function SGItem({
  heading,
  baseline,
  parent_element_uid,
  title: name,
  wa_name,
  link,
  media,
  preview,
  preview_alt,
  theme,
  vertical_align = "end",
  grid_index,
  cols,
  rfloading,
  triggerWa,
  lat,
  lng,
  quote,
  isAlt,
  author,
  _content_type_uid,
  range,
  href,
  taxonomies,
  asset,
  languages = [],
  onOpenFallbackModal,
  ...props
}) {
  const console = useConsole()
  const navigation = useNavigation()
  const dictionary = useDictionary()
  const locale = useLocale()
  const { quote: headquote } = heading ?? {}
  const { label, aria_label, href: link_href } = link ?? {}
  const norm_href = href || link_href || ""

  const rftext = useRef(null)
  const rfitem = useRef(null)
  const rflink = useRef(null)

  const [span, setSpan] = useState(-1)
  const [loaded, setLoaded] = useState(isLoaded(range.get()))
  const hasFallbackLanguages = languages.length > 0

  function updateXY(index) {
    const count = cols.get() || 1
    const denom = count * 2
    const alt = index % 2
    const swap = count % 2 === 0
    const x = index % count
    const rsindex = index % denom >= count && swap ? 1 - alt : alt
    const l = Math.floor(index / count)
    setSpan(rsindex)
    setProperty(rfitem.current, "--sg-item-index", index % (RANGE_INC - 1))
    setProperty(rfitem.current, "--sg-item-col", x + 1)
    setProperty(rfitem.current, "--sg-item-v-index", Math.floor(index / count))
    setProperty(rfitem.current, "--sg-offset", `var(--sg-offset-${x + 1})`)
    setProperty(rfitem.current, `--sg-item-row-${1 + rsindex}`, Math.floor(l / 2))
    setProperty(rfitem.current, `--sg-item-row-${2 - rsindex}`, Math.ceil(l / 2))
    setProperty(rfitem.current, "--sg-item-row-span", `var(--sg-row-span-${rsindex + 1})`)
    setProperty(rfitem.current, "--sg-ratio", `var(--sg-ratio-${rsindex + 1})`)
  }

  function onCols(v) {
    updateXY(grid_index)
  }
  useMotionValueEvent(cols, "change", onCols)

  useLayoutEffect(() => {
    updateXY(grid_index)
  }, [grid_index])

  function onClick(e) {
    e.preventDefault()

    if (hasFallbackLanguages) {
      onOpenFallbackModal?.({
        href,
        languages,
      })
    } else {
      rfloading.current = 1
      triggerWa(wa_name, (grid_index + 1).toString())
      navigation.navigate(navigation.localize(norm_href))
    }
  }

  function prevent(e) {
    e.preventDefault()
  }
  //  const ass = isAlt && preview_alt?.asset?.length > 0 ? preview_alt.asset : preview?.asset?.length > 0 ? preview.asset : []

  let arr = asset?.filter(({ resource_type: v }) => v === "video")
  arr = arr?.length > 0 ? arr : asset?.filter(({ resource_type: v }) => v === "image") ?? []

  arr = arr?.sort((a, b) => b.width / b.height - a.width / a.height).filter(({ width, height }) => width / height <= 1)

  function isLoaded(v) {
    return grid_index < v * RANGE_INC
  }
  function onRange(v) {
    setLoaded(isLoaded(v))
  }
  useMotionValueEvent(range, "change", onRange)

  useLayoutEffect(() => {
    if (!loaded) return
    updateXY(grid_index)
    setTimeout(() => setProperty(rfitem.current, "--sg-item-opacity", 1), 40)
  }, [loaded, grid_index])

  const focused = useMotionValue(0)
  function initObserver() {
    const { current: node } = rfitem
    if (!node) return

    const observer = new IntersectionObserver(
      ([{ intersectionRatio }]) => {
        focused.set(+(intersectionRatio >= 0.2))
      },
      { threshold: 0.2 }
    )
    observer.observe(node)

    return () => {
      if (observer) observer.unobserve(node)
    }
  }

  useLayoutEffect(initObserver, [loaded])

  const cardContent = (
    <Text ref={rftext}>
      {baseline && parent_element_uid ? <Kicker>{baseline}</Kicker> : null}
      {name && <Title dangerouslySetInnerHTML={{ __html: name }} />}
      {headquote && <Quote>{headquote}</Quote>}
      {/* SmallLink renders an <a>, which can't be nested inside CardButton (invalid HTML) */}
      {link && !hasFallbackLanguages && (
        <SmallLink className='inline' icon='none'>
          {label}
        </SmallLink>
      )}
      <CTA aria-hidden={true}>{`${dictionary.discoverMore()}`}</CTA>
    </Text>
  )

  return loaded ? (
    <Item
      ref={rfitem}
      style={{
        "--sg-item-v-align": vertical_align,
        "--sg-item-background": `var(--sg-item-background-${quote ? "quote" : "main"})`,
      }}
      className={`${asset?.[0] ? "dark-theme" : "light-theme"}${_content_type_uid === "data_quote" ? " quote-card" : ""}`}
      onClick={onClick}
    >
      {_content_type_uid === "data_quote" ? (
        quote ? (
          <CardAction className='sg-quote' ref={rflink} href={norm_href} aria-label={aria_label} onClick={prevent}>
            <Text>
              {author ? <Kicker>{author}</Kicker> : null}
              <Quote>{quote}</Quote>
              {link && (
                <SmallLink className='inline' icon='none'>
                  {label}
                </SmallLink>
              )}
            </Text>
          </CardAction>
        ) : null
      ) : (
        <>
          {span >= 0 ? (
            <Media>
              {arr[0]?.resource_type === "image" ? <SGImage sources={[arr?.[span] || arr[0]]} /> : null}
              {arr[0]?.resource_type === "video" ? <SGVideo sources={[arr?.[span] || arr[0]]} focused={focused} /> : null}
              {/* {image_cld?.sources?.length ? <SGImage image_cld={image_cld} sources={[image_cld?.sources?.[span]]} /> : null}
          {video_cld?.sources?.length ? <SGVideo video_cld={video_cld} sources={[video_cld?.sources?.[span]]} YP={YP}/> : null} */}
            </Media>
          ) : null}
          {hasFallbackLanguages ? (
            <CardButton
              ref={rflink}
              type='button'
              className='sg-card-action'
              aria-label={`${dictionary.discoverMore()} - ${name} - ${dictionary.changeLanguage()}`}
            >
              {cardContent}
            </CardButton>
          ) : (
            <CardAction ref={rflink} className='sg-card-action' href={norm_href} aria-label={`${dictionary.discoverMore()} - ${name}`} onClick={prevent}>
              {cardContent}
            </CardAction>
          )}
        </>
      )}
    </Item>
  ) : null
}
